import React, { useMemo } from 'react';
import { LoadingMessage } from 'js/components';
import SeriesRadarChart, {
  seriesItemsFromLegendMaps,
} from 'js/components/Charts/SeriesRadarChart';
import { getCategoryLookups } from 'js/utils/question-set';
import standardCohortKeys from 'js/utils/cohort-keys';
import { COMBINED_CHART_PALETTE_INDEX } from 'js/utils/color';
import { scoreNode } from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgRecommendations/utils';
import { buildOrgHistoryDataMap } from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgAssessmentHistory/buildOrgHistoryDataMap';
import historyStyles from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgAssessmentHistory/OrgAssessmentHistory.module.scss';

/**
 * Top-level combined radar for snapshot views. No org picker and no cohorts.
 */
export default function OrgSnapshotCombinedRadar({
  organization,
  assessmentTemplate,
  questionScores,
  catalog = null,
  isLoading = false,
  legendHeading = 'Combined',
  seriesName = '(Combined)',
  mode = 'dark',
  compact = false,
  hideLegend = false,
}) {
  const dataMap = useMemo(() => {
    if (!organization || !questionScores) {
      return null;
    }
    if (!assessmentTemplate && !catalog) {
      return null;
    }
    const scored = scoreNode(organization, questionScores, organization);
    return buildOrgHistoryDataMap(
      assessmentTemplate,
      scored,
      catalog,
      questionScores
    );
  }, [assessmentTemplate, catalog, organization, questionScores]);

  const categoryLookups = useMemo(() => {
    if (!dataMap) {
      return new Map();
    }
    return getCategoryLookups(
      [{ config: assessmentTemplate?.config }],
      dataMap.categories
    );
  }, [assessmentTemplate?.config, dataMap]);

  const categoryColorMap = useMemo(() => {
    const map = {};
    categoryLookups.forEach((value, key) => {
      map[key] = value.palette;
    });
    return map;
  }, [categoryLookups]);

  if (isLoading && !dataMap) {
    return <LoadingMessage iconSize="2x">Loading data&hellip;</LoadingMessage>;
  }

  const questionSets = dataMap?.questionSets ?? {};
  if (Object.keys(questionSets).length === 0) {
    if (compact) {
      return <p className="text-muted mb-0">No scored questions to plot.</p>;
    }
    return null;
  }

  const legendFields = [
    {
      key: standardCohortKeys.CUMULATIVE,
      paletteIndex: COMBINED_CHART_PALETTE_INDEX,
      name: seriesName,
    },
  ];
  const seriesDataMaps = {
    [standardCohortKeys.CUMULATIVE]: { questionSets },
  };

  return (
    <div className={compact ? undefined : historyStyles.root}>
      <SeriesRadarChart
        questionSets={questionSets}
        series={seriesItemsFromLegendMaps(legendFields, seriesDataMaps)}
        categoryLookups={categoryLookups}
        categoryColorMap={categoryColorMap}
        legendHeading={legendHeading}
        compact={compact}
        hideLegend={hideLegend || compact}
        mode={mode}
      />
    </div>
  );
}
